import SiteImage from "./SiteImage";
import { WLD_V3_ART } from "@/data/wldVolume3";
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { m as motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Menu, X, ArrowRight, ChevronDown, ExternalLink } from "lucide-react";

const CURRICULUM_FEATURED_IMG = `${import.meta.env.BASE_URL}${WLD_V3_ART.w1200}`;

type MegaKey = "curriculum" | "tools";

const NAV_LINKS: { label: string; href: string; external: boolean; mega: MegaKey | null }[] = [
  { label: "Home", href: "/", external: false, mega: null },
  { label: "About", href: "/about", external: false, mega: null },
  { label: "Curriculum", href: "/shop", external: false, mega: "curriculum" },
  { label: "Tools", href: "/tools", external: false, mega: "tools" },
  { label: "Blog", href: "/blog", external: false, mega: null },
];

const TOOLS_ITEMS = [
  {
    kicker: "Take care of yourself",
    name: "Leader Self-Assessment",
    desc: "A private, clinically-grounded mental-health snapshot. No account needed, results shown only to you.",
    path: "/self-assessment",
  },
  {
    kicker: "Know your students",
    name: "Ministry Health Survey",
    desc: "35 anonymous questions students answer via QR code, so you see how your group is really doing on a live dashboard.",
    path: "/ministry-survey",
  },
  {
    kicker: "Teach with confidence",
    name: "Curriculum Quiz",
    desc: "6 quick questions about your ministry, matched to the curriculum that fits where you actually are.",
    path: "/curriculum-quiz",
  },
  {
    kicker: "Equip your team",
    name: "The Art of Conversation",
    desc: "10 short video sessions and guides for leaders and parents to help young people open up.",
    path: "/training",
  },
];

const MOBILE_CURRICULUM_ITEMS = [
  { label: "Browse All Curriculum", href: "/shop", external: false },
  { label: "Whole Life Discipleship", href: "/wld", external: false },
  { label: "Build a Bundle", href: "/build-bundle", external: false },
  { label: "Curriculum Quiz", href: "/curriculum-quiz", external: false },
];

function AnnouncementBar() {
  return (
    <div className="bg-primary text-white text-center py-[11px] px-5 text-[13.5px] font-medium tracking-[0.01em]">
      Bundle 4, 8, or 12 series and build exactly what your ministry needs
      <Link href="/build-bundle" className="ml-2.5 font-bold border-b-[1.5px] border-white pb-[1px] hover:border-[#1A1A1A] transition-colors inline-flex items-center">
        Build Your Bundle <ArrowRight className="w-3 h-3 ml-1.5 translate-y-[1px]" />
      </Link>
    </div>
  );
}

function CurriculumMega({ close }: { close: () => void }) {
  return (
    <div className="w-full max-w-[1240px] mx-auto px-10 py-9 flex gap-14 items-stretch">
      <div className="flex-none w-80 flex flex-col justify-center gap-6">
        <h2 className="text-[34px] font-black text-[#1A1A1A] tracking-tight leading-none">Curriculum</h2>
        <p className="text-[#333333] text-[14.5px] leading-relaxed">
          Conversation-driven youth ministry curriculum where no topic is off-limits. Grab a complete curriculum volume or build a custom bundle.
        </p>
        <div className="flex flex-col gap-3">
          <Link
            href="/curriculum-quiz"
            onClick={close}
            className="inline-flex items-center justify-center px-6 py-3.5 rounded-[10px] bg-primary text-white font-extrabold text-[12.5px] uppercase tracking-[0.09em] hover:bg-[#B01E2C] transition-colors"
          >
            Take the Curriculum Quiz
          </Link>
          <Link
            href="/build-bundle"
            onClick={close}
            className="inline-flex items-center justify-center px-6 py-3.5 rounded-[10px] border-2 border-[#1A1A1A]/20 text-[#1A1A1A] font-extrabold text-[12.5px] uppercase tracking-[0.09em] hover:border-primary hover:text-primary transition-colors"
          >
            Build Your Bundle
          </Link>
        </div>
      </div>

      <div className="flex-1 flex flex-col gap-4">
        <div className="flex items-center gap-6">
          <Link
            href="/wld"
            onClick={close}
            className="flex items-center gap-2.5 text-primary font-bold text-[15px] hover:underline group"
          >
            <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-[10px] font-black flex-shrink-0 group-hover:scale-110 transition-transform">WLD</span>
            Whole Life Discipleship
          </Link>
          <span className="w-px h-6 bg-primary/30" />
          <Link
            href="/shop"
            onClick={close}
            className="flex items-center gap-2 text-primary font-bold text-[15px] hover:underline"
          >
            Check out All Curriculum
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <Link
          href="/wld"
          onClick={close}
          className="relative rounded-lg overflow-hidden flex-1 min-h-[260px] block group shadow-xl"
        >
          <SiteImage loading="eager"
            src={CURRICULUM_FEATURED_IMG}
            alt="Whole Life Discipleship Volume 3 curriculum"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-5 left-6 text-white">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] opacity-75 mb-1">Presale · Releases Jan 1, 2027</p>
            <p className="text-xl font-black leading-tight">Whole Life Discipleship Volume 3</p>
            <p className="text-sm font-bold text-primary mt-1 flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
              Preorder Now <ArrowRight className="w-4 h-4" />
            </p>
          </div>
        </Link>
      </div>
    </div>
  );
}

function ToolsMega({ close }: { close: () => void }) {
  return (
    <div className="w-full max-w-[1440px] mx-auto px-8 xl:px-10 py-9 flex gap-8 xl:gap-10 items-stretch">
      <div className="flex-none w-64 xl:w-72 flex flex-col justify-center gap-6">
        <h2 className="text-[34px] font-black text-[#1A1A1A] tracking-tight leading-none">Free Tools</h2>
        <p className="text-[#333333] text-[14.5px] leading-relaxed">
          Four free, research-backed tools built for the realities of ministry life. No account needed to start.
        </p>
        <Link
          href="/tools"
          onClick={close}
          className="inline-flex items-center justify-center px-6 py-3.5 rounded-[10px] bg-primary text-white font-extrabold text-[12.5px] uppercase tracking-[0.09em] hover:bg-[#B01E2C] transition-colors"
        >
          Explore All Tools
        </Link>
      </div>

      <div className="flex-1 grid grid-cols-4 gap-4 xl:gap-5">
        {TOOLS_ITEMS.map((tool) => (
          <Link
            key={tool.path}
            href={tool.path}
            onClick={close}
            className="group flex flex-col gap-3 rounded-lg border border-[#1A1A1A]/10 p-5 xl:p-6 hover:border-primary hover:shadow-lg transition-all"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">{tool.kicker}</p>
            <p className="text-lg font-black text-[#1A1A1A] leading-tight">{tool.name}</p>
            <p className="text-[13.5px] text-[#333333] leading-relaxed flex-1">{tool.desc}</p>
            <span className="inline-flex items-center gap-1.5 text-primary font-bold text-sm group-hover:gap-2.5 transition-all">
              Start free <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

const TOOLS_PATHS = ["/tools", "/self-assessment", "/ministry-survey", "/curriculum-quiz", "/assessment", "/training"];

export default function SiteNav({ isSignedIn, setLocation }: { isSignedIn: boolean; setLocation: (path: string) => void }) {
  const [location] = useLocation();
  const showLogin = TOOLS_PATHS.some((p) => location === p || location.startsWith(`${p}/`));
  const [activeMega, setActiveMega] = useState<null | MegaKey>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<null | MegaKey>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openMega = (key: MegaKey) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveMega(key);
  };
  const closeMega = () => {
    closeTimer.current = setTimeout(() => setActiveMega(null), 130);
  };
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  const clearMega = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveMega(null);
  };

  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <motion.nav
      initial={{ y: -64, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="sticky top-0 z-50 bg-white/95 backdrop-blur-[14px] border-b border-[#1A1A1A]/10"
      onMouseLeave={closeMega}
    >
      <AnnouncementBar />
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 h-[74px] flex items-center justify-between gap-4">
        {/* Logo */}
        <Link href="/" className="flex-shrink-0 select-none flex items-center" onMouseEnter={clearMega}>
          <SiteImage loading="eager" src={`${import.meta.env.BASE_URL}reframe-logo.png`} alt="Reframe Youth" className="h-[30px]" />
        </Link>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-[34px]">
          {NAV_LINKS.map((link) => {
            const isActive = link.mega !== null && activeMega === link.mega;
            const sharedClass = "relative text-[14.5px] font-semibold text-[#333333] hover:text-primary transition-colors flex items-center gap-1 py-[26px]";
            const inner = (
              <>
                {link.label}
                {link.mega && (
                  <motion.span animate={{ rotate: isActive ? 180 : 0 }} transition={{ duration: 0.2 }} className="inline-flex">
                    <ChevronDown className="w-3.5 h-3.5 text-[#333333]/60" />
                  </motion.span>
                )}
              </>
            );
            if (link.mega) {
              if (!link.external) {
                return (
                  <Link
                    key={link.label}
                    href={link.href}
                    className={sharedClass}
                    onMouseEnter={() => openMega(link.mega as MegaKey)}
                    onClick={() => setActiveMega(null)}
                  >
                    {inner}
                  </Link>
                );
              }
              return (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={sharedClass}
                  onMouseEnter={() => openMega(link.mega as MegaKey)}
                  onClick={(e) => e.preventDefault()}
                >
                  {inner}
                </a>
              );
            }
            if (!link.external) {
              return (
                <Link key={link.label} href={link.href} className={sharedClass} onMouseEnter={clearMega}>
                  {inner}
                </Link>
              );
            }
            return (
              <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className={sharedClass} onMouseEnter={clearMega}>
                {inner}
              </a>
            );
          })}
        </div>

        {/* Right CTA */}
        <div className="hidden lg:flex items-center gap-4" onMouseEnter={clearMega}>
          {showLogin && (isSignedIn ? (
            <Button variant="outline" className="border-[#1A1A1A]/20 text-[#1A1A1A] font-extrabold text-[12.5px] tracking-[0.09em] uppercase px-5 py-[22px] rounded-[10px] hover:bg-black/5" onClick={() => setLocation("/dashboard")}>
              My Dashboard
            </Button>
          ) : (
            <Button variant="outline" className="border-[#1A1A1A]/20 text-[#1A1A1A] font-extrabold text-[12.5px] tracking-[0.09em] uppercase px-5 py-[22px] rounded-[10px] hover:bg-black/5" onClick={() => setLocation("/sign-in")}>
              Log In
            </Button>
          ))}
          <Button className="bg-primary hover:bg-[#B01E2C] text-white font-extrabold text-[12.5px] tracking-[0.09em] uppercase px-[19px] py-[20px] rounded-[10px] transition-colors" asChild>
            <Link href="/shop">
              Buy Curriculum
            </Link>
          </Button>
        </div>

        {/* Mobile menu toggle */}
        <button
          className="lg:hidden p-2 rounded-md text-slate-600 hover:bg-slate-50 transition-colors"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
          aria-controls="mobile-site-navigation"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Desktop mega menu panel */}
      <AnimatePresence>
        {activeMega !== null && (
          <motion.div
            key={activeMega}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="hidden lg:block absolute left-0 right-0 top-full bg-white border-b border-[#1A1A1A]/10 shadow-2xl z-50"
            onMouseEnter={cancelClose}
            onMouseLeave={closeMega}
          >
            {activeMega === "curriculum" && <CurriculumMega close={() => setActiveMega(null)} />}
            {activeMega === "tools" && <ToolsMega close={() => setActiveMega(null)} />}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            id="mobile-site-navigation"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "100vh", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden fixed inset-x-0 top-[114px] bg-white border-t border-slate-100 overflow-y-auto pb-32"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {NAV_LINKS.map((link) => {
                const linkClass = "text-xl font-bold text-[#1A1A1A] hover:text-primary transition-colors";
                if (link.mega) {
                  const open = mobileExpanded === link.mega;
                  const items = link.mega === "curriculum"
                    ? MOBILE_CURRICULUM_ITEMS
                    : [
                        { label: "All Tools", href: "/tools", external: false },
                        ...TOOLS_ITEMS.map((t) => ({ label: t.name, href: t.path, external: false })),
                      ];
                  return (
                    <React.Fragment key={link.label}>
                      <button
                        className={`${linkClass} flex items-center justify-between w-full text-left`}
                        onClick={() => setMobileExpanded(open ? null : link.mega)}
                      >
                        {link.label}
                        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        </motion.span>
                      </button>
                      <AnimatePresence>
                        {open && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.22 }}
                            className="overflow-hidden pl-4 -mt-2 flex flex-col gap-4"
                          >
                            {items.map((item) =>
                              item.external ? (
                                <a
                                  key={item.label}
                                  href={item.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-between text-[15px] font-semibold text-[#333333] hover:text-primary transition-colors"
                                  onClick={() => setMobileOpen(false)}
                                >
                                  {item.label} <ExternalLink className="w-3.5 h-3.5 opacity-40" />
                                </a>
                              ) : (
                                <Link
                                  key={item.label}
                                  href={item.href}
                                  className="flex text-[15px] font-semibold text-[#333333] hover:text-primary transition-colors"
                                  onClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
                                >
                                  {item.label}
                                </Link>
                              )
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </React.Fragment>
                  );
                }
                if (!link.external) {
                  return (
                    <Link key={link.label} href={link.href} className={linkClass} onClick={() => setMobileOpen(false)}>
                      {link.label}
                    </Link>
                  );
                }
                return (
                  <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className={linkClass} onClick={() => setMobileOpen(false)}>
                    {link.label}
                  </a>
                );
              })}
              <div className="pt-6 border-t border-slate-100 flex flex-col gap-4">
                <Button className="w-full bg-primary hover:bg-[#B01E2C] text-white font-extrabold text-[13.5px] tracking-[0.09em] uppercase py-[18px] rounded-[10px]" asChild>
                  <Link href="/shop">
                    Buy Curriculum
                  </Link>
                </Button>
                {showLogin && (isSignedIn ? (
                  <Button variant="outline" className="w-full border-black/20 text-[#1A1A1A] font-extrabold text-[13.5px] tracking-[0.09em] uppercase py-[18px] rounded-[10px]" onClick={() => { setMobileOpen(false); setLocation("/dashboard"); }}>
                    My Dashboard
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full border-black/20 text-[#1A1A1A] font-extrabold text-[13.5px] tracking-[0.09em] uppercase py-[18px] rounded-[10px]" onClick={() => { setMobileOpen(false); setLocation("/sign-in"); }}>
                    Log In
                  </Button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
